/*!

=========================================================
* Argon Dashboard PRO React - v1.2.0
=========================================================

* Product Page: https://www.creative-tim.com/product/argon-dashboard-pro-react
* Copyright 2021 Creative Tim (https://www.creative-tim.com)

* Coded by Creative Tim

=========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.

*/
import React, { useEffect, useState, useRef } from 'react';
import { useHistory } from 'react-router';
import { Link } from 'react-router-dom';
import { useSelector } from 'react-redux';

import ReactToPrint from 'react-to-print';
import BootstrapTable from 'react-bootstrap-table-next';
import paginationFactory from 'react-bootstrap-table2-paginator';
import ToolkitProvider, { Search } from 'react-bootstrap-table2-toolkit';
import {
  Button,
  Card,
  Container,
  Row,
  Col,
} from 'reactstrap';
import ReactTooltip from 'react-tooltip';
import Swal from 'sweetalert2';
import moment from 'moment';
import XLSX from 'xlsx';

import SimpleHeader from 'components/Headers/SimpleHeader.js';

import { getMembers, deleteMemberInfo, checkPassword } from 'apis/admin/userApi';

const pagination = paginationFactory({
  page: 1,
  alwaysShowAllBtns: true,
  showTotal: true,
  withFirstAndLast: false,
  sizePerPageRenderer: ({ options, currSizePerPage, onSizePerPageChange }) => (
    <div className="dataTables_length" id="datatable-basic_length">
      <label>
        {
          <select
            name="datatable-basic_length"
            aria-controls="datatable-basic"
            className="form-control form-control-sm"
            onChange={(e) => onSizePerPageChange(e.target.value)}
          >
            <option value="10">10</option>
            <option value="25">25</option>
            <option value="50">50</option>
            <option value="100">100</option>
          </select>
        }
      </label>
    </div>
  ),
  paginationTotalRenderer: (from, to, size) => (
    <span className="react-bootstrap-table-pagination-total">
      { from } - { to } / Total { size }
    </span>
  ),
});

const { SearchBar } = Search;

function MemberList() {
  const user = useSelector(state => state.user);
  const componentRef = useRef();
  const [memberList, setMemberList] = useState([]);
  const [checked, setChecked] = useState([]);
  const history = useHistory();

  // 회원 목록 조회
  const getMemberList = async () => {
    await getMembers()
      .then(response => {
        const membersData = response.data.result;

        if (membersData) {
          setMemberList(membersData);
        }
      })
      .catch(error => {
        console.error('error occured in Members.js - getMemberList() ', error);

        Swal.fire({
          icon: 'error',
          title: '회원 목록을 불러올 수 없습니다.',
          text: '잠시 후 다시 시도해주세요.',
        }).then(() => {
          return;
        });
      });
  };

  // 회원 삭제
  const onDeleteMember = () => {
    Swal.fire({
      title: '해당 회원을 비활성화 하시겠습니까?',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: '회원 비활성화',
      cancelButtonText: '취소',
      confirmButtonColor: '#f5365c',
    }).then(async (value) => {
      if (value.isConfirmed) {
        const { value: password } = await Swal.fire({
          title: '비밀번호를 입력하세요.',
          input: 'password',
          inputLabel: '관리자 계정의 비밀번호를 입력하세요.',
          inputAttributes: {
            autocapitalize: 'off',
            autocorrect: 'off',
          },
          showCancelButton: true,
          confirmButtonText: '확인',
          cancelButtonText: '취소',
        });

        if (password) {
          await checkPassword({ userid: user && user.userData.userid, password })
            .then(async (response) => {
              if (response.data.success) {
                const joinAllData = checked.length < 2 ? checked[0] : checked.join('-');
                
                await deleteMemberInfo(joinAllData)
                  .then(async (response) => {
                    if (response.data.success) {
                      setChecked([]);
                      await getMemberList();
                    }
                  })
                  .catch((error) => {
                    console.error('error occured in Member.js - onDeleteMember() ', error);
      
                    Swal.fire({
                      title: '잠시 후 다시 시도해주세요.',
                      icon: 'warning',
                    });
                  });
              } else {
                Swal.fire({
                  title: '비밀번호가 맞지 않습니다.',
                  icon: 'error',
                });
              }
            })
            .catch((error) => {
              console.error('error occured in Member.js - onDeleteMember() ', error);

              Swal.fire({
                title: '잠시 후 다시 시도해주세요.',
                icon: 'warning',
              });
            });
        }
      } else {
        Swal.close();
      }
    });
  };

  // 엑셀 다운로드
  const exportDataToExcel = () => {
    let dataToExport = [...memberList];
    dataToExport.reverse();

    dataToExport.forEach((val) => {
      val.is_admin = val.is_admin === 1 ? 'O' : 'X';
      val.receive_email = val.receive_email === 1 ? '수신' : '수신 거부';
      val.receive_sms = val.receive_sms === 1 ? '수신' : '수신 거부';
      val.email_cert = val.email_cert === 1 ? '인증' : '미인증';
      val.denied = val.denied === 1 ? '차단' : '허용';
      val.register_datetime = moment(val.register_datetime).format('YYYY[-]MM[-]DD HH:mm');
      val.lastlogin_datetime = val.lastlogin_datetime ? moment(val.lastlogin_datetime).format('YYYY[-]MM[-]DD HH:mm') : '';
    });

    let worksheet = XLSX.utils.json_to_sheet(dataToExport);
    const new_workbook = XLSX.utils.book_new();

    const columnsVerKo = [
      '번호',
      '아이디',
      '이메일',
      '실명',
      '닉네임',
      '그룹',
      '최고 관리자 여부',
      '레벨',
      '가입일',
      '마지막 로그인 시간',
      '이메일 인증',
      '이메일 수신',
      '문자 수신',
      '차단 여부',
    ];

    columnsVerKo.forEach((val, idx) => {
      const changeCell = XLSX.utils.encode_cell({ c: idx, r:0 });
      worksheet[changeCell].v = val;
    });

    XLSX.utils.book_append_sheet(new_workbook, worksheet, 'SheetJS');
    XLSX.writeFile(new_workbook, `사용자_목록_${moment(Date.now()).format('YYYY[-]MM[-]DD[_]HH[:]mm')}.xlsx`);
  };

  useEffect(() => {
    getMemberList();
  }, []);

  // 테이블 컬럼
  const columns = [
    {
      dataField: 'id',
      text: '번호',
      sort: true,
      headerAlign: 'center',
      align: 'center',
    },
    {
      dataField: 'userid',
      text: '아이디',
      sort: true,
      headerAlign: 'center',
      align: 'center',
      formatter: (cell, row) => {
        if (row.is_admin !== 1) {
          return <span>{cell}</span>;
        }

        return (
          <div>
            <span>{cell}</span>
            <span className="ml-1" style={{ fontSize: '11px' }}>
              <i
                className="fas fa-user-cog"
                data-tip="최고 관리자"
                data-place="right"
                data-effect="solid"
              />
              <ReactTooltip />
            </span>
          </div>
        );
      },
    },
    {
      dataField: 'username',
      text: '이름',
      sort: true,
      headerAlign: 'center',
      align: 'center',
    },
    {
      dataField: 'nickname',
      text: '닉네임',
      sort: true,
      headerAlign: 'center',
      align: 'center',
    },
    {
      dataField: 'email',
      text: '이메일',
      sort: true,
      headerAlign: 'center',
      align: 'center',
    },
    {
      dataField: 'level',
      text: '레벨',
      sort: true,
      headerAlign: 'center',
      align: 'center',
    },
    {
      dataField: 'member_group',
      text: '그룹',
      sort: false,
      headerAlign: 'center',
      align: 'center',
    },
    {
      dataField: 'register_datetime',
      text: '가입일',
      sort: true,
      headerAlign: 'center',
      align: 'center',
      formatter: (cell) =>
        (cell && moment(cell).format('YYYY[-]MM[-]DD')) || '-',
    },
    {
      dataField: 'lastlogin_datetime',
      text: '최근 로그인',
      sort: true,
      headerAlign: 'center',
      align: 'center',
      formatter: (cell) =>
        (cell && moment(cell).format('YYYY[-]MM[-]DD HH:mm')) || '-',
    },
    // {
    //   dataField: '',
    //   text: '인증/메일수신/문자수신',
    //   sort: false,
    //   headerAlign: 'center',
    //   align: 'center',
    //   formatter: (cell, row) => (
    //     <span>
    //       <input
    //         type="checkbox"
    //         checked={row.email_cert === 1}
    //         readOnly
    //         className="mr-1"
    //       />
    //       <input
    //         type="checkbox"
    //         checked={row.receive_email === 1}
    //         readOnly
    //         className="mr-1"
    //       />
    //       <input type="checkbox" checked={row.receive_sms === 1} readOnly />
    //     </span>
    //   ),
    // },
    // {
    //   dataField: 'denied',
    //   text: '차단',
    //   sort: true,
    //   headerAlign: 'center',
    //   align: 'center',
    //   formatter: (cell) =>
    //     cell === 0 ? (
    //       <span>
    //         -
    //       </span>
    //     ) : (
    //       <span className="text-danger">
    //         <i className="fas fa-ban" />
    //       </span>
    //     ),
    // },
    {
      dataField: '',
      text: '편집',
      sort: false,
      searchable: false,
      headerAlign: 'center',
      align: 'center',
      formatter: (cell, row) => (
        <Link to={`/admin/members/edit/${row.id}`}>
          <i className="fas fa-edit text-primary" />
        </Link>
      ),
    },
  ];

  // 데이터 선택
  const selectRow = {
    mode: 'checkbox',
    clickToSelect: false,
    onSelect: (row) => {
      const currentChecked = checked.indexOf(row.id);
      const newChecked = [...checked];

      if (currentChecked === -1) {
        newChecked.push(row.id);
      } else {
        newChecked.splice(currentChecked, 1);
      }

      setChecked(newChecked);
    },
    onSelectAll: (isSelect, rows) => {
      if (isSelect) {
        const newChecked = [];
        rows.map((val) => newChecked.push(val.id));
  
        setChecked(newChecked);
      } else {
        setChecked([]);
      }
    },
  };

  return (
    <>
      <SimpleHeader name="회원 관리" parentName="회원 설정" />
      <Container className="mt--6" fluid>
        <Row>
          <div className="col">
            <Card>
              <ToolkitProvider
                data={memberList}
                keyField="id"
                columns={columns}
                search
              >
                {(props) => (
                  <div className="py-4 table-responsive">
                    <Container fluid>
                      <Row>
                        <Col xs={12} sm={6}>
                          <div
                            id="datatable-basic_filter"
                            className="dataTables_filter"
                          >
                            <label>
                              <span className="text-sm mr-2">
                                <i className="fas fa-search text-muted" />
                              </span>
                              <SearchBar
                                className="form-control-sm"
                                {...props.searchProps}
                                placeholder="아이디, 이메일, 이름..."
                              />
                            </label>
                          </div>
                        </Col>
                        <Col xs={12} sm={6}>
                          {user && user.userData && user.userData.isAdmin && (
                            <div className="px-2 pb-1 float-right">
                              <>
                                {checked.length > 0 && (
                                  <Button
                                    className="buttons-copy buttons-html5"
                                    color="danger"
                                    size="sm"
                                    onClick={onDeleteMember}
                                  >
                                    회원 비활성화
                                  </Button>
                                )}
                                {/* <Button
                                  className="buttons-copy buttons-html5"
                                  color="success"
                                  size="sm"
                                  onClick={exportDataToExcel}
                                >
                                  엑셀 다운로드
                                </Button>
                                <ReactToPrint
                                  trigger={() => (
                                    <Button
                                      color="default"
                                      size="sm"
                                      className="buttons-copy buttons-html5"
                                    >
                                      인쇄
                                    </Button>
                                  )}
                                  content={() => componentRef.current}
                                /> */}
                                <Button
                                  className="buttons-copy buttons-html5"
                                  color="primary"
                                  size="sm"
                                  onClick={() => history.push({
                                    pathname: '/admin/members/create',
                                  })}
                                >
                                  회원 등록
                                </Button>
                              </>
                            </div>
                          )}
                        </Col>
                      </Row>
                    </Container>
                    <BootstrapTable
                      ref={componentRef}
                      {...props.baseProps}
                      bootstrap4={true}
                      pagination={pagination}
                      bordered={false}
                      selectRow={selectRow}
                      noDataIndication={() => <div className="text-center text-muted">데이터가 없습니다.</div>}
                    />
                  </div>
                )}
              </ToolkitProvider>
            </Card>
          </div>
        </Row>
      </Container>
    </>
  );
}

export default MemberList;
